import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const trucksInformationApi = createApi({
  reducerPath: "trucksInformationApi",
  // keepUnusedDataFor: 60, // Keeps data for 60 seconds
  // refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        // Include token in request header
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    fetchFn: async (input, init) => {
      if (!input) {
        // Handle invalid input gracefully
        return new Response(null, {
          status: 400,
          statusText: "Invalid request",
        });
      }

      const response = await fetch(input, init);

      if (response.status === 204) {
        return new Response(JSON.stringify([]), { status: 200 });
      }

      return response;
    },
  }),
  endpoints: (builder) => ({
    fetchAllTrucks: builder.query({
      query: (location) => `/truck/portal/get/all-trucks/${location}`,
    }),
    getTruckById: builder.query({
      query: (truckId) => `/trucks/portal/get/truck/${truckId}`,
    }),
    getTrucksByStatus: builder.query({
      query: (status) => `/trucks/portal/get/trucks-by-status?status=${status}`,
    }),
    getTrucksByDriver: builder.query({
      query: (driverId) => `/trucks/portal/get/trucks-by-driver/${driverId}`,
    }),
    searchTrucks: builder.query({
      query: (searchParams) => {
        const { keyword, limit = 50 } = searchParams;
        if (keyword && keyword.length >= 2) {
          return `/trucks/portal/search?keyword=${keyword}&limit=${limit}`;
        }
        return ""; // Return empty string for invalid queries
      },
      // Skip query for short keywords
      skip: (searchParams) => !searchParams.keyword || searchParams.keyword.length < 2,
    }),
    fetchInventoryActiveTechs: builder.query({
      query: () => `/truck/portal/get/inventory-active-techs`,
    }),
    fetchVehicleEditOptions: builder.query({
      query: () => `/truck/portal/get/vehicle-edit-options`,
    }),
    updateVehicleRecord: builder.mutation({
      query: (payload) => ({
        url: `/truck/portal/update/vehicle-record`,
        method: "POST",
        body: payload,
      }),
    }),
    // Glitch #555 — add an image or PDF to a vehicle's Documents. Multipart, so
    // the body is FormData and the browser sets the boundary itself (never set
    // Content-Type by hand here).
    uploadVehicleDocument: builder.mutation({
      query: ({ vehicleId, file }) => {
        const body = new FormData();
        body.append("vehicleId", vehicleId);
        body.append("file", file);
        return {
          url: `/truck/portal/update/vehicle-document`,
          method: "POST",
          body,
        };
      },
    }),
    createTruckDamageReport: builder.mutation({
      query: (damageReportData) => ({
        url: `/truck/portal/update/truck-damage-report`,
        method: "POST",
        body: damageReportData,
      }),
    }),
  }),
});

export const {
  useFetchVehicleEditOptionsQuery,
  useUpdateVehicleRecordMutation,
  useUploadVehicleDocumentMutation,
  useFetchAllTrucksQuery,
  useGetTruckByIdQuery,
  useGetTrucksByStatusQuery,
  useGetTrucksByDriverQuery,
  useSearchTrucksQuery,
  useFetchInventoryActiveTechsQuery,
  useCreateTruckDamageReportMutation,
} = trucksInformationApi;